import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ShieldCheck, FileArrowDown, CheckCircle, WarningCircle, XCircle } from "@phosphor-icons/react";

const DECISIONS = [
  { key: "approved", label: "Approve", icon: CheckCircle, cls: "bg-emerald-600 hover:bg-emerald-700" },
  { key: "changes_requested", label: "Request changes", icon: WarningCircle, cls: "bg-amber-600 hover:bg-amber-700" },
  { key: "declined", label: "Decline", icon: XCircle, cls: "bg-red-600 hover:bg-red-700" },
];

export default function ReviewSignOff() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(null);

  const load = useCallback(() => {
    api.get(`/review/sign/${token}`)
      .then((r) => { setData(r.data); setComment(r.data.comment || ""); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail || "This link is invalid or has expired.")));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async (decision) => {
    setBusy(decision);
    try {
      const { data: res } = await api.post(`/review/sign/${token}`, { decision, comment });
      setDone(res.decision);
      toast.success("Your decision was recorded. Thank you.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#071B3E] p-6" data-testid="signoff-error">
      <div className="bg-white max-w-md w-full p-8 text-center">
        <XCircle size={40} className="mx-auto text-red-600 mb-3" />
        <h1 className="text-xl font-bold text-[#071B3E]">Link unavailable</h1>
        <p className="text-sm text-muted-foreground mt-2">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#071B3E] text-white">Loading…</div>;

  const closed = ["finalized", "cancelled"].includes(data.review_status) || !!done || data.decision !== "pending";

  return (
    <div className="min-h-screen bg-[#071B3E] py-10 px-4" data-testid="signoff-page">
      <div className="max-w-2xl mx-auto bg-white">
        <div className="bg-[#071B3E] text-white px-7 py-5 flex items-center gap-3">
          <ShieldCheck size={28} weight="fill" />
          <div>
            <div className="font-bold text-lg leading-tight">ClaroShield — Document Sign-off</div>
            <div className="text-xs text-white/70">{data.is_manager ? "Manager escalation review" : "Reviewer sign-off"}</div>
          </div>
        </div>

        <div className="p-7 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Investigation</div>
            <div className="text-lg font-semibold text-[#071B3E]" data-testid="signoff-title">{data.investigation_title}</div>
            <div className="text-sm text-muted-foreground">{data.standard_id} {(data.requirement_ids || []).join(", ")}</div>
          </div>

          <div className="text-sm">Hi <b>{data.reviewer_name}</b>, please open each document, review with Word
            <i> Track Changes / All Markup</i>, then record your decision below.</div>

          {data.instructions && (
            <div className="bg-muted/60 border border-border p-3 text-sm" data-testid="signoff-instructions">{data.instructions}</div>
          )}

          <div>
            <div className="text-sm font-semibold mb-2">Documents to review</div>
            <div className="space-y-2" data-testid="signoff-documents">
              {(data.documents || []).map((d) => (
                <a key={d.id || d.url} href={(/^https?:\/\//i.test(d.url || "") ? d.url : "#")} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 border border-border px-3 py-2 text-sm text-blue-700 hover:bg-muted/50 break-all">
                  <FileArrowDown size={16} /> {d.label || d.url}
                </a>
              ))}
            </div>
          </div>

          {closed ? (
            <div className="border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="signoff-recorded">
              {done || data.decision !== "pending"
                ? <>Your decision <b>{(done || data.decision).replace("_", " ")}</b> has been recorded. You may close this page.</>
                : <>This review is <b>{data.review_status}</b> and no longer accepts sign-offs.</>}
            </div>
          ) : (
            <>
              <Textarea data-testid="signoff-comment" value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Comments (optional) — note any requested edits or context" className="rounded-none min-h-[90px]" />
              <div className="flex flex-wrap gap-3">
                {DECISIONS.map((d) => (
                  <Button key={d.key} data-testid={`signoff-${d.key}-btn`} disabled={!!busy}
                    onClick={() => submit(d.key)} className={`rounded-none text-white ${d.cls}`}>
                    <d.icon size={16} className="mr-1.5" weight="fill" />
                    {busy === d.key ? "Saving…" : d.label}
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="px-7 py-4 border-t border-border text-xs text-muted-foreground">
          The goal of this review is accuracy and completeness — changes are tracked in the source document.
        </div>
      </div>
    </div>
  );
}
